import { createNewCandidateSchedule, createPhotoRenewalSchedule, getScheduleTalentPrefill } from "@/api/schedule";
import { ScheduleType, type PhotoMaterialType, type SchedulePrefill } from "@/api/schedule/models";
import { Button, Portal } from "@/components/common";
import { InputDatePicker, InputDropdown, InputRow } from "@/components/common/inputs";
import { ModalBase } from "@/components/common/modals";
import { ScheduleForm } from "@/components/features/schedule/ScheduleForm";
import { PhotoRenewalResponsibleSection } from "@/components/features/schedule/PhotoRenewalResponsibleSection";
import { PhotoRenewalTalentSection } from "@/components/features/schedule/PhotoRenewalTalentSection";
import { type UseFormControl, useForm } from "@/hooks/common/use-form";
import { showToast } from "@/providers";
import { formatDateInSaoPauloTimeZone } from "@/utils/date";
import { scheduleTimeOptions, talentOriginOptions } from "@/utils/features/schedule/options";
import { verifyIfTalentIsAdult } from "@/utils/features/talent/validations";

import { buildNewCandidateSchedulePayload } from "./new-candidate-schedule";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { addDays, parseISO } from "date-fns";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

interface CreateScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type CreateScheduleFormFields =
  | "scheduleType"
  | "candidateOrigin"
  | "talentCode"
  | "photoMaterialType"
  | "candidateFullName"
  | "candidateBirthday"
  | "candidateEmail"
  | "candidatePhone"
  | "candidateSecondaryPhone"
  | "responsibleFullName"
  | "responsibleEmail"
  | "responsiblePhone"
  | "responsibleSecondaryPhone"
  | "scheduleDate"
  | "scheduleTime"
  | "responsibleIndex";

const initialValues = {
  scheduleType: null,
  candidateOrigin: null,
  talentCode: "",
  photoMaterialType: null,
  candidateFullName: "",
  candidateBirthday: null,
  candidateEmail: "",
  candidatePhone: "",
  candidateSecondaryPhone: "",
  responsibleFullName: "",
  responsibleEmail: "",
  responsiblePhone: "",
  responsibleSecondaryPhone: "",
  scheduleDate: null,
  scheduleTime: "",
  responsibleIndex: null,
};

const photoMaterialOptions = ["B1", "B2", "B3", "B4", "BD"].map((value) => ({ label: value, value }));
const scheduleTypeOptions = [
  { label: "Renovação de fotos", value: ScheduleType.PHOTO_RENEWAL },
  { label: "Novo candidato", value: ScheduleType.NEW_CANDIDATE },
];

const ModalPopperContainer = ({ children }: { children?: ReactNode }) => <Portal>{children}</Portal>;

export const CreateScheduleModal = ({ isOpen, onClose }: CreateScheduleModalProps): JSX.Element => {
  const queryClient = useQueryClient();
  const [prefill, setPrefill] = useState<SchedulePrefill | null>(null);
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const [isLoadingPrefill, setIsLoadingPrefill] = useState(false);
  const activeLookupIdentifier = useRef(0);

  const resetModal = () => {
    control.reset(initialValues);
    setPrefill(null);
    setPrefillError(null);
    setIsLoadingPrefill(false);
  };

  const { action, control, isSubmitting } = useForm<{ id: string }, CreateScheduleFormFields>({
    initialValues,
    fields: {
      scheduleType: { validation: "none" },
      candidateOrigin: { validation: "newCandidateOrigin" },
      talentCode: { validation: "scheduleTalentCode" },
      photoMaterialType: { validation: "photoMaterialType" },
      candidateFullName: { validation: "newCandidateFullName" },
      candidateBirthday: { validation: "newCandidateBirthday" },
      candidateEmail: { validation: "emailOptional" },
      candidatePhone: { validation: "phoneOptional" },
      candidateSecondaryPhone: { validation: "phoneOptional" },
      responsibleFullName: { validation: "newCandidateResponsibleFullName" },
      responsibleEmail: { validation: "newCandidateResponsibleEmail" },
      responsiblePhone: { validation: "newCandidateResponsiblePhone" },
      responsibleSecondaryPhone: { validation: "none" },
      scheduleDate: { validation: "required" },
      scheduleTime: { validation: "required" },
      responsibleIndex: { validation: "none" },
    },
    handleSubmit: async (data) => {
      if (data.scheduleType === ScheduleType.NEW_CANDIDATE) {
        return createNewCandidateSchedule(
          buildNewCandidateSchedulePayload({
            scheduleDate: data.scheduleDate,
            scheduleTime: data.scheduleTime,
            candidateFullName: data.candidateFullName,
            candidateBirthday: data.candidateBirthday,
            candidateOrigin: data.candidateOrigin,
            candidateEmail: data.candidateEmail,
            candidatePhone: data.candidatePhone,
            candidateSecondaryPhone: data.candidateSecondaryPhone,
            responsibleFullName: data.responsibleFullName,
            responsibleEmail: data.responsibleEmail,
            responsiblePhone: data.responsiblePhone,
            responsibleSecondaryPhone: data.responsibleSecondaryPhone,
            isMinor: Boolean(isMinor),
          }),
        );
      }

      if (!prefill) {
        setPrefillError("Informe um codigo de talento válido antes de criar o agendamento.");
        throw new Error("Dados do talento são obrigatórios.");
      }

      return createPhotoRenewalSchedule({
        talentId: prefill.id,
        responsibleId: prefill.responsibles?.[Number(data.responsibleIndex ?? 0)]?.id,
        photoMaterialType: data.photoMaterialType as PhotoMaterialType,
        scheduleDate: buildScheduleDate(data.scheduleDate, data.scheduleTime),
      });
    },
    options: {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["schedule", "agenda"], refetchType: "all" }),
          queryClient.invalidateQueries({ queryKey: ["schedule", "stage-counts"], refetchType: "all" }),
        ]);
        showToast({ type: "success", title: "Agendamento criado com sucesso." });
        resetModal();
        onClose();
      },
      onSubmitError: (error) => {
        const errorMessage = getScheduleErrorMessage(error);
        if (!errorMessage) {
          return false;
        }

        showToast({ type: "error", title: errorMessage });
        return true;
      },
    },
  });

  const scheduleType = control.watch("scheduleType") as ScheduleType | null;
  const talentCode = String(control.watch("talentCode") ?? "").trim();
  const candidateBirthday = control.watch("candidateBirthday") as Date | string | null;
  const responsibleIndex = Number(control.watch("responsibleIndex") ?? 0);
  const selectedResponsible = prefill?.responsibles?.[responsibleIndex];
  const isPhotoRenewal = scheduleType === ScheduleType.PHOTO_RENEWAL;
  const isNewCandidate = scheduleType === ScheduleType.NEW_CANDIDATE;
  const hasValidCandidateBirthday = isNewCandidate && isValidDate(candidateBirthday);
  const isMinor = isPhotoRenewal
    ? prefill?.responsibles !== undefined
    : hasValidCandidateBirthday && !verifyIfTalentIsAdult(candidateBirthday);
  const showTalentOrCandidateFields = isPhotoRenewal || isNewCandidate;

  const responsibleOptions = useMemo(
    () =>
      prefill?.responsibles?.map((responsible, index) => ({
        label: responsible.fullName,
        value: String(index),
      })) ?? [],
    [prefill?.responsibles],
  );

  useEffect(
    function handlesTalentFieldsPrefillOnTalentCodeLookUp() {
      const lookupIdentifier = activeLookupIdentifier.current + 1;
      activeLookupIdentifier.current = lookupIdentifier;

      if (!isOpen || !isPhotoRenewal) {
        setPrefill(null);
        setPrefillError(null);
        setIsLoadingPrefill(false);
        return;
      }

      if (!talentCode) {
        setPrefill(null);
        setPrefillError(null);
        setIsLoadingPrefill(false);
        return;
      }

      const abortController = new AbortController();
      setPrefill(null);
      setPrefillError(null);
      const timeout = window.setTimeout(async () => {
        await lookupTalent(talentCode, abortController.signal, lookupIdentifier);
      }, 1000);

      return () => {
        window.clearTimeout(timeout);
        abortController.abort();
      };
    },
    [isOpen, isPhotoRenewal, talentCode],
  );

  const lookupTalent = async (code: string, signal: AbortSignal, lookupIdentifier: number) => {
    setIsLoadingPrefill(true);
    try {
      const talentPrefill = await getScheduleTalentPrefill(code, signal);

      if (activeLookupIdentifier.current !== lookupIdentifier) {
        return;
      }

      setPrefill(talentPrefill);
      control.setValue("responsibleIndex", talentPrefill.responsibles?.length ? "0" : null);
    } catch {
      if (signal.aborted || activeLookupIdentifier.current !== lookupIdentifier) {
        return;
      }

      setPrefill(null);
      setPrefillError("Nenhum talento foi encontrado para este codigo.");
    } finally {
      if (activeLookupIdentifier.current === lookupIdentifier) {
        setIsLoadingPrefill(false);
      }
    }
  };

  const handleScheduleTypeChange = (nextScheduleType: string | null) => {
    control.reset({ ...initialValues, scheduleType: nextScheduleType });
    setPrefill(null);
    setPrefillError(null);
    setIsLoadingPrefill(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const minimumDate = parseISO(formatDateInSaoPauloTimeZone());
  const maximumDate = addDays(minimumDate, 6);

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={handleClose}
      className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[650px] flex-col gap-4 overflow-y-auto p-6 sm:w-full"
    >
      <div className="text-center">
        <h3 className="font-medium">Novo agendamento</h3>
        <p>Preencha os dados abaixo para criar um novo agendamento.</p>
      </div>
      <form id="photo-renewal-schedule-form" className="form-flow" onSubmit={action}>
        <section>
          <h2 className="mb-4 text-base font-medium text-title">Tipo de agendamento</h2>
          <InputRow>
            <InputDropdown
              control={control}
              field="scheduleType"
              label="Categoria"
              options={scheduleTypeOptions}
              noOptionSelectedText="Selecionar"
              usePortalMenu
              menuClassName="!z-[1000]"
              onChange={handleScheduleTypeChange}
            />
            {isNewCandidate && (
              <InputDropdown
                control={control}
                field="candidateOrigin"
                label="Origem"
                options={talentOriginOptions}
                noOptionSelectedText="Selecionar"
                usePortalMenu
                menuClassName="!z-[1000]"
              />
            )}
            {isPhotoRenewal && (
              <InputDropdown
                control={control}
                field="photoMaterialType"
                label="Material contratado"
                options={photoMaterialOptions}
                noOptionSelectedText="Selecionar"
                usePortalMenu
                menuClassName="!z-[1000]"
              />
            )}
          </InputRow>
        </section>

        {showTalentOrCandidateFields && (
          <>
            {isPhotoRenewal ? (
              <PhotoRenewalTalentSection
                control={control}
                prefill={prefill}
                isLoading={isLoadingPrefill}
                error={prefillError}
                isMinor={Boolean(isMinor)}
              />
            ) : (
              <ScheduleForm
                control={control}
                mode="create"
                isMinor={Boolean(isMinor)}
                minimumDate={minimumDate}
                maximumDate={maximumDate}
                popperContainer={ModalPopperContainer}
                usePortalMenu
              />
            )}

            {isPhotoRenewal && isMinor && (
              <PhotoRenewalResponsibleSection
                control={control}
                options={responsibleOptions}
                responsible={selectedResponsible}
              />
            )}

            {isPhotoRenewal && <ScheduleFields control={control} maximumDate={maximumDate} minimumDate={minimumDate} />}
          </>
        )}
      </form>
      <div className="flex flex-col-reverse gap-3 sm:flex-row">
        <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting} full>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          form="photo-renewal-schedule-form"
          isLoading={isSubmitting}
          disabled={isSubmitting || (isPhotoRenewal && !prefill) || !scheduleType}
          full
        >
          Criar agendamento
        </Button>
      </div>
    </ModalBase>
  );
};

function ScheduleFields({
  control,
  minimumDate,
  maximumDate,
}: {
  control: UseFormControl;
  minimumDate: Date;
  maximumDate: Date;
}) {
  return (
    <section>
      <h2 className="mb-4 text-base font-medium text-title">Agendamento</h2>
      <InputRow>
        <InputDatePicker
          control={control}
          field="scheduleDate"
          label="Data"
          placeholder="DD/MM/AAAA (sábado)"
          minDate={minimumDate}
          maxDate={maximumDate}
          hideRemoveFilterButton
          popperContainer={ModalPopperContainer}
          popperClassName="!z-[1000]"
        />
        <InputDropdown
          control={control}
          field="scheduleTime"
          label="Horário"
          options={scheduleTimeOptions}
          noOptionSelectedText="Selecionar"
          usePortalMenu
          menuClassName="!z-[1000]"
        />
      </InputRow>
    </section>
  );
}

function isValidDate(value: Date | string | null): value is Date | string {
  return !!value && !Number.isNaN(new Date(value).getTime());
}

function getScheduleErrorMessage(error: unknown): string | null {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const message = error.response?.data?.message;
  if (typeof message === "string") {
    return message;
  }
  if (Array.isArray(message)) {
    return message.join(" ");
  }

  return "Não foi possível criar o agendamento. Tente novamente.";
}

function buildScheduleDate(scheduleDate: Date | string, scheduleTime: string): Date {
  const date = new Date(scheduleDate);
  const [hours, minutes] = scheduleTime.split(":").map(Number);
  date.setHours(hours, minutes, 0, 0);
  return date;
}
